import { IonAvatar, IonChip, IonLabel } from "@ionic/react";
import { IoCheckmark } from "react-icons/io5";
import { MdPersonOff } from "react-icons/md";
import { subPhotoPlaceholderPNG } from "swing-assets";
import { Colors, Icon } from "swing-components";

import { translate } from "~utils";

/***** Types *****/
export type ChipFilledDisplay = "empty" | "partial" | "full";

/**
 * Defines the three potential SubStates
 *
 * - Without sub details
 * - With only sub profile image
 * - With sub profile image and name
 */
type SubStates =
  // Without any sub details
  | object
  // With only sub profile image details
  | {
      sub: {
        profileImageUrl: string;
      };
    }
  // With sub profile image, name and onSubNameClick callback
  | {
      sub: {
        profileImageUrl?: string;
        name: string;
        onSubNameClick: () => void;
      };
    };

/**
 * Defines the FILLED status which can support one of the three SubStates
 */
type ChipFilledStatus = {
  status: "FILLED";
  display: ChipFilledDisplay;
} & SubStates;

/**
 * Defined the COMPLETED status which only supports a single sub state.
 */
export type ChipCompletedStatus = {
  status: "COMPLETED";
  display: ChipFilledDisplay;
} & SubStates;

/**
 * Defined the UNFILLEDstatus which only supports a single sub state.
 */
export type ChipUnfilledStatus = {
  status: "UNFILLED";
};

/**
 * Defined the CANCELLED status which only supports a single sub state.
 */
export type ChipCancelledStatus = {
  status: "CANCELLED";
};

/**
 * Defined the WAITLIST status which supports two sub-states:
 * - With one waiting sub
 * - With more than one waiting sub
 */
export type ChipWaitlistStatus = {
  status: "WAITLIST";
  waitlistSubs: Array<{ profileImageUrl: string }>;
};

/**
 * Defined the CLOSED status which only supports a single sub state.
 */
export type ChipClosedStatus = {
  status: "CLOSED";
};

/**
 * Defined the PENDING status which only supports a single sub state.
 */
export type ChipPendingStatus = {
  status: "PENDING";
  sub?: { profileImageUrl: string };
};

type ChipStatus =
  | ChipFilledStatus
  | ChipUnfilledStatus
  | ChipCompletedStatus
  | ChipCancelledStatus
  | ChipWaitlistStatus
  | ChipPendingStatus
  | ChipClosedStatus;

type ChipProps = ChipStatus & {
  hasLink?: boolean;
};

/***** Constants *****/
// Maps the chips status to the Ion-Chip color (these colors are named vs being
// a hex). See https://ionicframework.com/docs/api/chip#colors
const CHIP_STATUS_TO_COLOR: Record<ChipProps["status"], string> = {
  FILLED: "success", // Green
  UNFILLED: "tertiary", // Purple
  // FIXME: This color does not match the color in the Figma design, see
  // https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?node-id=1667%3A890&mode=dev
  COMPLETED: "success", // Green
  CANCELLED: "danger",
  WAITLIST: "warning",
  PENDING: "warning",
  CLOSED: "dark",
};

/**
 * Component | Chip
 *
 * Renders a chip/tag/badge to handle various statuses
 *
 * @Figma https://www.figma.com/file/GPTogwtUYLEcr9yz9skF5M/Swing-Ionic-Design-System?type=design&node-id=656-18368
 */
export function Chip(props: ChipProps) {
  const { status, hasLink } = props;

  return (
    <IonChip
      color={CHIP_STATUS_TO_COLOR[status]}
      css={{
        width: "fit-content", // To make sure that the chip doesn't stretch
        // Removes the default margin which throws off alignment when used
        // inline
        margin: 0,
        gap: 4,
        // The padding used by IonChip is 7px which on a 32px height chip, will
        // leave less than 20px for the content. This fixes that.
        paddingTop: 6,
        paddingBottom: 6,
        cursor: hasLink ? "pointer" : "auto",
        // Remove the ability for users to select the text within this
        // container. When attempting to double click on the chip,
        // nothing will be highlighted.
        userSelect: "none",
      }}
    >
      {status === "FILLED" && <FilledOrCompletedStatusBody {...props} />}
      {status === "UNFILLED" && <UnfilledStatusBody {...props} />}
      {status === "COMPLETED" && <FilledOrCompletedStatusBody {...props} />}
      {status === "CANCELLED" && <CancelledStatusBody {...props} />}
      {status === "WAITLIST" && <WaitlistStatusBody {...props} />}
      {status === "PENDING" && <PendingStatusBody {...props} />}
      {status === "CLOSED" && <ClosedBody {...props} />}
    </IonChip>
  );
}

/***** Body Components ******/
/**
 * Body Component | FilledOrCompletedStatusBody
 *
 * Renders the body content for the FILLED or Completd status
 *
 * The filled and completed chips are identical apart from
 * the description string.
 */

function FilledOrCompletedStatusBody(props: ChipFilledStatus | ChipCompletedStatus) {
  const { display, status } = props;

  const sub = "sub" in props ? props.sub : undefined;
  const name = sub && "name" in sub ? sub.name : undefined;
  const onSubNameClick = sub && "onSubNameClick" in sub ? sub.onSubNameClick : undefined;

  // "empty" - checkmark with "filled" text
  // "full" - avatar / image with "filled by" and clickable sub name

  return display === "empty" ? (
    <>
      {/* Left Icon */}
      <IoCheckmark size="24px" css={{ display: "inline-block" }} />

      {/* Label */}
      <IonLabel className="swing-text">{translate("CHIP_LABEL_FILLED")}</IonLabel>
    </>
  ) : display === "full" ? (
    <>
      {/* Left Icon */}
      <IonAvatar css={{ margin: 0 }}>
        <img src={sub?.profileImageUrl || subPhotoPlaceholderPNG} alt="avatar" />
      </IonAvatar>

      {/* Label */}
      <IonLabel
        className="swing-text"
        css={{ display: "flex", gap: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        <span css={{ flexShrink: 0 }}>
          {status === "FILLED"
            ? translate("CHIP_LABEL_FILLED_WITH_NAME")
            : translate("CHIP_LABEL_COMPLETED_BY")}
        </span>
        {/* TODO: consider IonButton, which accepts a href vs. only onClick */}
        <button
          css={{
            backgroundColor: "transparent",
            color: Colors.blue500,
            flexShrink: 1,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          onClick={() => onSubNameClick?.()}
        >
          {name}
        </button>
      </IonLabel>
    </>
  ) : null;
}

/**
 * Body Component | UnfilledStatusBody
 *
 * Renders the body content for the UNFILLED status
 */
function UnfilledStatusBody(
  // We are defining these `props` for consistency
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: ChipUnfilledStatus,
) {
  return (
    <>
      {/* Left Icon */}
      <MdPersonOff size="24px" css={{ display: "inline-block" }} />

      {/* Label */}
      <IonLabel className="swing-text">{translate("CHIP_LABEL_UNFILLED")}</IonLabel>
    </>
  );
}

/**
 * Body Component | CancelledStatusBody
 *
 * Renders the body content for the CANCELLED status
 */
function CancelledStatusBody(
  // We are defining these `props` for consistency
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: ChipCancelledStatus,
) {
  return (
    <>
      {/* Left Icon */}
      <Icon name="Delete" color={Colors.red500} />

      {/* Label */}
      <IonLabel className="swing-text">{translate("CHIP_LABEL_CANCELLED")}</IonLabel>
    </>
  );
}

/**
 * Body Component | WaitlistStatusBody
 *
 * Renders the body content for the Waitlist status
 */
function WaitlistStatusBody(props: ChipWaitlistStatus) {
  const { waitlistSubs } = props;

  return (
    <>
      {/* Left Icon */}
      <IonAvatar css={{ margin: 0 }}>
        <img src={subPhotoPlaceholderPNG} alt="user default icon" />
      </IonAvatar>

      {/* Label */}
      {/* For accessibility, the Waitlist text is black */}
      <IonLabel className="swing-text" css={{ color: "var(--ion-color-warning-contrast)" }}>
        {waitlistSubs.length > 0
          ? translate("CHIP_LABEL_WAITLIST_MORE_THAN_ONE", { count: waitlistSubs.length })
          : translate("CHIP_LABEL_WAITLIST")}
      </IonLabel>
    </>
  );
}

/**
 * Body Component | PendingStatusBody
 *
 * Renders the body content for the PENDING status
 */
function PendingStatusBody(props: ChipPendingStatus) {
  const { sub } = props;
  return (
    <>
      {/* Left Icon */}
      {sub && sub.profileImageUrl ? (
        <IonAvatar css={{ margin: 0 }}>
          <img src={sub.profileImageUrl} alt="avatar" />
        </IonAvatar>
      ) : (
        <IonAvatar css={{ margin: 0 }}>
          <img src={subPhotoPlaceholderPNG} alt="user default icon" />
        </IonAvatar>
      )}

      {/* Label */}
      {/* override text color for more contrast */}
      <IonLabel className="swing-text" css={{ color: "var(--ion-color-warning-contrast)" }}>
        {translate("CHIP_LABEL_PENDING")}
      </IonLabel>
    </>
  );
}

/**
 * Body Component | PendingStatusBody
 *
 * Renders the body content for the CLOSED status
 */
function ClosedBody(
  // We are defining these `props` for consistency
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: ChipClosedStatus,
) {
  return (
    <>
      {/* Left Icon */}
      <Icon name="Person Off" color={Colors.slate400} />

      {/* Label */}
      {/* override text color for more contrast */}
      <IonLabel className="swing-text" css={{ color: "var(--ion-color-warning-contrast)" }}>
        {translate("CHIP_LABEL_CLOSED")}
      </IonLabel>
    </>
  );
}
